import {
  AppVersion,
  Button,
  ButtonColor, Colors,
  getMediaDevices,
  PopupButtonType,
  PopupIconType,
  PopupOnCloseData,
  PopupRef,
  showPopup
} from "@kalyzee/kast-web-sdk/web-components";
import React, { createRef, ForwardedRef, useImperativeHandle, useRef } from "react";
import { Settings } from "../../helpers/settings";
import { useSettings } from "../../hooks/settings";
import { useRequestAdminPin } from "../../hooks/utils";
import { usePageContextRef } from "../navigation/PageContext";
import styles from "./SettingsPopup.module.css";
import Versions from "./Versions";

export interface SettingsPopupProps {}

export interface SettingsPopupRef {
  openPopup: () => void;
}

const SettingsPopup = React.forwardRef(({}: SettingsPopupProps, forwardRef: ForwardedRef<SettingsPopupRef | undefined>) => {
  const [settings, saveSettings] = useSettings();
  const requestAdminPin = useRequestAdminPin();
  const pageContextRef = usePageContextRef();
  const isOpened = useRef<boolean>(false);

  const tryOpenPopup = () => {
    requestAdminPin((success) => {
      if (success) openPopup();
    });
  };

  const openPopup = () => {
    if (isOpened.current) return;
    isOpened.current = true;
    const textareaRef = createRef<HTMLTextAreaElement>();
    const advancedSettingsContainerRef = createRef<HTMLDivElement>();
    const popupRef = createRef<PopupRef>();
    const popupIndex = showPopup({
      title: (
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          Paramètres
        </div>
      ),
      style: { width: "70%" },
      content: (
        <div className={styles.popupSettingsContent}>
          <div className={styles.popupSettingsButtonsContainer}>
            <Button
              color={ButtonColor.PURPLE}
              title="Media Devices"
              className={styles.button}
              onPress={async () => {
                const devices = await getMediaDevices("both", "input");
                showPopup({
                  title: "Devices",
                  content: <div className={styles.popupDevicesContent}>{JSON.stringify(devices, null, 4)}</div>,
                  buttons: [{ type: PopupButtonType.OK, element: "OK" }],
                });
              }}
            />
          </div>
          <textarea
            ref={textareaRef}
            style={{ display: "none" }}
            className={styles.popupSettingsTextArea}
            defaultValue={JSON.stringify(settings, null, 4)}
            spellCheck={false}
          />
          <div ref={advancedSettingsContainerRef} className={styles.popupAdvancedSettingsContainer}>
            <Button
              title="Afficher les paramètres avancés"
              style={{ backgroundColor: "grey" }}
              className={styles.button}
              onPress={() => {
                if (textareaRef.current) textareaRef.current.style.display = "flex";
                if(advancedSettingsContainerRef.current) advancedSettingsContainerRef.current.style.display = 'none';
                popupRef.current?.updateButtons([
                  { type: PopupButtonType.CANCEL, element: "Annuler" },
                  {
                    type: PopupButtonType.VALIDATE,
                    element: <div style={{ color: Colors.getMainGreen() }}>Valider</div>,
                    onClick: () => {
                      let newSettings: object | undefined;
                      let error: string | undefined;
                      try {
                        newSettings = JSON.parse(textareaRef.current?.value ?? "");
                      } catch (err) {
                        error = (err as any)?.message;
                      }
                      if (!newSettings) {
                        alert("Impossible de sauvegarder les paramètres\nUne erreur est apparue durant la sauvegarde des paramètres : " + error);
                      }
                      return newSettings ? true : false;
                    },
                  },
                ]);
              }}
            />
          </div>
          <Versions />
          <AppVersion />
        </div>
      ),
      buttons: [{ type: PopupButtonType.OK, element: 'OK'}],
      onClose: (data: PopupOnCloseData) => {
        isOpened.current = false;
        if (data.closeFromButton?.type === PopupButtonType.VALIDATE) {
          // save settings
          let newSettings: Settings | undefined;
          let error: string | undefined;
          try {
            newSettings = saveSettings(JSON.parse(textareaRef.current?.value ?? ""));
          } catch (err) {
            error = (err as any)?.message;
          }

          if (newSettings) {
            showPopup({
              title: "Appliquer les changements",
              iconTitle: PopupIconType.SUCCESS,
              content: "Vous avez modifié les paramétres. Voulez-vous rafraîchir la page pour appliquer tout de suite vos modifications ?",
              buttons: [
                { type: PopupButtonType.CANCEL, element: "Non" },
                { type: PopupButtonType.VALIDATE, element: <div style={{ color: Colors.getMainGreen() }}>Oui</div> },
              ],
              onClose: (data: PopupOnCloseData) => {
                if (data.closeFromButton?.type === PopupButtonType.VALIDATE) {
                  window.location.reload();
                }
              },
            });
          } else {
            showPopup({
              title: "Impossible de sauvegarder les paramètres",
              iconTitle: PopupIconType.ERROR,
              content: "Une erreur est apparue durant la sauvegarde des paramètres : " + error,
              buttons: [{ type: PopupButtonType.OK, element: "OK" }],
            });
          }
        }
      },
    }, popupRef);
  };

  useImperativeHandle(forwardRef, () => ({
    openPopup: tryOpenPopup,
  }));

  return null;
});

export default SettingsPopup;
